import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fmtDateHuman } from "../../dateUtils";

/**
 * RaceCountdown — Today screen, main upcoming race banner.
 *
 * Pure presentational atom. Parent passes:
 *   - race meta (name, distance label, ISO start date),
 *   - "today" ISO date (so days-until is testable without Date.now()),
 *   - up to 4 forecast chips (label + value + tone + tagline),
 *   - optional weather chip,
 *   - onOpen / onPacing callbacks.
 *
 * Day computation uses local-noon to avoid DST flipping the count.
 *
 * When daysUntil <= 0 → "race week 🟢" or "today" copy without negative
 * numbers; once race day has passed by > 1 day, hide via empty-state
 * branch (parent decides whether to mount).
 */

export type ForecastTone = "green" | "amber" | "red" | "muted" | "accent";

export interface RaceForecastChip {
  label: string;
  value: string;
  caption?: string | null;
  tone?: ForecastTone;
}

export interface RaceCountdownProps {
  /** Race headline, e.g. "BERLIN HALF". */
  name: string;
  /** ISO date "YYYY-MM-DD" of the start. */
  startDate: string;
  /** "Today" ISO date for testable countdown math. */
  todayDate: string;
  distanceLabel: string;
  /** Pre-formatted local time "HH:MM" or null. */
  startTime?: string | null;
  weather?: string | null;
  forecasts?: RaceForecastChip[];
  onOpen?: () => void;
  onPacing?: () => void;
  className?: string;
}

export function computeDaysUntil(startISO: string, todayISO: string): number {
  // Parse YYYY-MM-DD as UTC noon to avoid DST flips.
  const [sy, sm, sd] = startISO.split("-").map(Number);
  const [ty, tm, td] = todayISO.split("-").map(Number);
  if (!sy || !ty) return 0;
  const start = Date.UTC(sy, sm - 1, sd, 12);
  const today = Date.UTC(ty, tm - 1, td, 12);
  return Math.round((start - today) / 86_400_000);
}

const TONE_COLORS: Record<ForecastTone, string> = {
  green: "var(--ok)",   // не лайм: он снят с роли статуса канон-сессией 24.07
  amber: "var(--warning-500)",
  red: "var(--danger-500)",
  muted: "var(--fg-muted)",
  accent: "var(--indigo-300)",
};

export function RaceCountdown({
  name,
  startDate,
  todayDate,
  distanceLabel,
  startTime,
  weather,
  forecasts,
  onOpen,
  onPacing,
  className = "",
}: RaceCountdownProps) {
  const { t, i18n } = useTranslation("dashboard");
  const days = useMemo(() => computeDaysUntil(startDate, todayDate), [startDate, todayDate]);

  const isToday = days === 0;
  const isPast = days < 0;
  const showLabel = isToday
    ? t("race.today", { defaultValue: "Сегодня" })
    : isPast
    ? t("race.past", { defaultValue: "Прошёл" })
    : days === 1
    ? t("race.tomorrow", { defaultValue: "Завтра" })
    : t("race.days", { defaultValue: "Дней" });

  return (
    <div
      data-testid="race-countdown"
      data-days={days}
      data-is-today={isToday ? "true" : "false"}
      className={`rounded-card border border-accent/35 bg-gradient-to-br from-accent/[0.06] to-bg-surface p-5 ${className}`}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-[3px] h-3.5 rounded-sm bg-accent inline-block" />
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-accent font-bold">
          {t("race.section_label", { defaultValue: "Главный старт" })} · {name}
        </span>
      </div>

      <div className="flex gap-6 items-start flex-wrap">
        <div className="flex-shrink-0">
          <div
            data-testid="race-countdown-days"
            className="race-week-hero text-[64px] font-extrabold text-accent leading-none tabular-nums num-display"
          >
            {Math.abs(days)}
          </div>
          <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
            {showLabel}
          </div>
        </div>

        <div className="flex-1 min-w-[200px]">
          <div className="flex gap-4 mb-3 flex-wrap">
            <div>
              <div className="text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-bold">
                {t("race.distance", { defaultValue: "Дистанция" })}
              </div>
              <div className="mt-1 text-sm font-semibold text-fg-primary">{distanceLabel}</div>
            </div>
            <div>
              <div className="text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-bold">
                {t("race.start", { defaultValue: "Старт" })}
              </div>
              <div className="mt-1 text-sm font-semibold text-fg-primary tabular-nums">
                {fmtDateHuman(startDate, i18n?.language ?? "ru")}
                {startTime ? ` · ${startTime}` : ""}
              </div>
            </div>
            {weather && (
              <div>
                <div className="text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-bold">
                  {t("race.weather", { defaultValue: "Погода" })}
                </div>
                <div className="mt-1 text-sm font-semibold text-fg-primary">{weather}</div>
              </div>
            )}
          </div>

          {forecasts && forecasts.length > 0 && (
            <div data-testid="race-countdown-forecasts" className="flex flex-wrap gap-2">
              {forecasts.map((f) => (
                <div
                  key={f.label}
                  data-testid={`race-countdown-forecast-${f.label}`}
                  className="rounded-md bg-bg-raised px-3 py-2 border border-bg-border"
                >
                  <div className="text-[9px] uppercase tracking-[0.18em] text-fg-muted font-bold">
                    {f.label}
                  </div>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span
                      className="text-[17px] font-bold tabular-nums num-display"
                      style={{ color: TONE_COLORS[f.tone ?? "accent"] }}
                    >
                      {f.value}
                    </span>
                    {f.caption && (
                      <span
                        className="text-[9px] uppercase tracking-[0.14em] font-bold"
                        style={{ color: TONE_COLORS[f.tone ?? "muted"] }}
                      >
                        {f.caption}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(onOpen || onPacing) && (
          <div className="flex-shrink-0 flex flex-col gap-2">
            {onOpen && (
              <button
                type="button"
                data-testid="race-countdown-open"
                onClick={onOpen}
                aria-label={t("race.cta_open_aria", { defaultValue: "Открыть страницу гонки" })}
                className="px-5 py-3 rounded-md bg-accent text-accent-on font-bold text-[13px] hover:brightness-110 transition"
              >
                {t("race.cta_open", { defaultValue: "Открыть гонку" })}
              </button>
            )}
            {onPacing && (
              <button
                type="button"
                data-testid="race-countdown-pacing"
                onClick={onPacing}
                aria-label={t("race.cta_pacing_aria", { defaultValue: "Открыть пейсинг-план" })}
                className="px-4 py-2.5 rounded-md border border-bg-border text-fg-secondary text-[12.5px] hover:bg-bg-raised transition"
              >
                {t("race.cta_pacing", { defaultValue: "Пейсинг план" })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
